import { createFileRoute } from '@tanstack/react-router'
import { PageShell } from '@/features/public-site/page-shell'
import { SimpleHero, HeroBadges, PricingCta, Panel } from '@/features/public-site/sections'

const stats = [
  { value: '20+', label: 'MCP 工具' },
  { value: '本地', label: 'stdio bridge' },
  { value: '5+', label: '兼容客户端' },
  { value: '0', label: '云端依赖' },
]

const capabilities = [
  {
    title: '读取当前项目',
    description: '获取已打开项目的素材清单、时间线状态和转录内容，作为编辑计划的上下文。',
  },
  {
    title: '生成编辑计划',
    description: '根据你的描述产出结构化的编辑步骤，逐步执行并汇报进度。',
  },
  {
    title: '转录与字幕',
    description: '调用 transcribe_media 生成转录，再转为时间线字幕轨。',
  },
  {
    title: '去口癖与删静音',
    description: 'remove_filler_words 清理口癖，删除静音区间，让节奏更紧凑。',
  },
  {
    title: '编辑时间线',
    description: 'trim / split / duplicate / ripple_delete 等操作直接作用于当前项目。',
  },
  {
    title: '导出与分享',
    description: '生成导出任务或创建公开分享链接，把成片交出去。',
  },
]

export const Route = createFileRoute('/mcp')({
  component: McpPage,
})

function McpPage() {
  return (
    <PageShell>
      <SimpleHero
        eyebrow="MCP 服务"
        title="让本机 AI 客户端"
        highlighted="协助操作当前项目"
        description="在本地启动 MCP Agent，通过 Bridge 连接当前已打开的 JIAN 编辑项目。Cursor、Claude 等兼容客户端可以读取项目、生成编辑计划、转录、去口癖、删静音、编辑时间线和导出。"
        buttonLabel="免费开始"
        secondaryLabel="查看文档"
        secondaryHref="/help/mcp-automation"
      />

      <HeroBadges
        items={['Claude', 'Cursor', 'ChatGPT', 'VS Code', 'Gemini CLI']}
      />

      {/* Stats */}
      <section className="mx-auto mt-16 max-w-4xl px-4 sm:px-6">
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          {stats.map((stat) => (
            <div key={stat.label} className="rounded-xl border border-border bg-card p-6 text-center">
              <div className="text-3xl font-bold text-primary">{stat.value}</div>
              <div className="mt-1 text-xs text-muted-foreground">{stat.label}</div>
            </div>
          ))}
        </div>

        {/* How it works */}
        <Panel className="mt-8">
          <div className="text-sm font-semibold uppercase tracking-wider text-primary">
            工作原理
          </div>
          <h2 className="mt-3 text-xl font-bold text-foreground">
            本地 Agent + Bridge，操作已打开的项目
          </h2>
          <p className="mt-3 text-sm text-muted-foreground">
            MCP 不需要把视频上传到第三方服务。Agent 运行在你本机，通过本地 Bridge
            连接浏览器中已打开的 JIAN 编辑器，直接读写当前项目的素材和时间线。
          </p>

          {/* Code example */}
          <div className="mt-6 rounded-xl bg-background p-4">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <span className="size-2 rounded-full bg-primary" />
              在 Cursor / Claude 中对当前项目说
            </div>
            <p className="mt-3 text-sm text-muted-foreground">
              "帮我转录这段音频，去掉口癖和静音，然后把字幕加到时间线上"
            </p>
            <pre className="mt-4 overflow-x-auto rounded-lg bg-black/40 p-4 text-xs text-foreground">
{`# JIAN MCP 依次调用本地工具：
transcribe_media       # 生成词级转录
remove_filler_words    # 识别并移除口癖区间
insert_transcript_as_captions  # 转为时间线字幕轨

# 所有操作作用于当前已打开的项目，无需上传。`}
            </pre>
          </div>
        </Panel>

        {/* Capabilities */}
        <div className="mt-12 text-center">
          <h2 className="text-2xl font-bold text-foreground">支持的编辑能力</h2>
          <p className="mx-auto mt-3 max-w-md text-sm text-muted-foreground">
            以下能力均可通过 MCP 工具调用，作用于当前打开的编辑项目。
          </p>
        </div>

        <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {capabilities.map((cap) => (
            <div key={cap.title} className="rounded-xl border border-border bg-card p-5">
              <h3 className="text-sm font-semibold text-foreground">{cap.title}</h3>
              <p className="mt-2 text-xs leading-6 text-muted-foreground">{cap.description}</p>
            </div>
          ))}
        </div>

        <div className="mt-16 border-y border-border py-12">
          <p className="text-xs font-semibold tracking-wider text-primary">QUICK START</p>
          <h2 className="mt-3 text-3xl font-semibold text-foreground">三步连接，开始本地 AI 剪辑。</h2>
          <div className="mt-8 grid gap-4 md:grid-cols-3">
            {[
              ['01', '打开 JIAN 项目', '登录后打开一个真实项目，并保持编辑器页面在线。'],
              ['02', '启动 MCP Agent', '安装包后运行 fastcut-mcp；默认通过本机 4174 端口连接 Bridge。'],
              ['03', '检查并执行小修改', '运行 doctor，确认连接后先让 AI 做一个可见的小修改。'],
            ].map(([number, title, copy]) => <div key={number} className="border border-border bg-card p-5"><span className="text-xs font-bold text-primary">STEP {number}</span><h3 className="mt-8 font-semibold">{title}</h3><p className="mt-2 text-sm leading-6 text-muted-foreground">{copy}</p></div>)}
          </div>
          <pre className="mt-5 overflow-x-auto bg-black/70 p-5 text-sm text-[#b8f28e]">npm install -g @fastcut-video/mcp-agent{`\n`}fastcut-mcp --doctor{`\n`}fastcut-mcp</pre>
        </div>
      </section>

      <PricingCta />
    </PageShell>
  )
}
